import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Send, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function AdminBroadcast() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/broadcasts").then(({ data }) => setList(data || [])).catch(() => setList(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and details are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", { title: title.trim(), body: body.trim() });
      setList((prev) => [data, ...(Array.isArray(prev) ? prev : [])]);
      setTitle(""); setBody("");
      toast.success("Broadcast sent to all users");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Broadcast failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/broadcasts/${toDelete.broadcast_id}`);
      setList((prev) => prev.filter((b) => b.broadcast_id !== toDelete.broadcast_id));
      toast.success("Broadcast removed");
      setToDelete(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-broadcast">
      <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="broadcast-composer">
        <h3 className="font-display font-bold flex items-center gap-2 mb-1"><Megaphone className="w-4 h-4 text-primary-c" /> New broadcast</h3>
        <p className="text-xs mb-4" style={{ color: "var(--text-2)" }}>Sent to every user's notification bell instantly.</p>
        <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. New feature: Score entry)"
          className="w-full rounded-xl bg-surface glow-border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400 mb-3" style={{ color: "var(--text)" }} />
        <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Details…"
          className="w-full rounded-xl bg-surface glow-border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-400 resize-none" style={{ color: "var(--text)" }} />
        <button data-testid="broadcast-submit" onClick={send} disabled={busy || !title.trim() || !body.trim()}
          className="btn-glow w-full rounded-xl py-3 mt-3 font-bold flex items-center justify-center gap-2 disabled:opacity-50 tracking-wider">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> BROADCAST</>}
        </button>
      </div>

      <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="broadcast-history">
        <h3 className="font-display font-bold flex items-center gap-2 mb-4"><Megaphone className="w-4 h-4 text-primary-c" /> Sent broadcasts</h3>
        {list === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>
        ) : list === false ? (
          <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load broadcasts.</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: "var(--text-2)" }}>No broadcasts sent yet.</p>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto">
            {list.map((b) => (
              <div key={b.broadcast_id} data-testid={`broadcast-item-${b.broadcast_id}`} className="rounded-xl p-3" style={{ background: "var(--bg)" }}>
                <div className="flex items-start justify-between gap-2">
                  <span className="font-bold text-sm">{b.title}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] mono" style={{ color: "var(--text-2)" }}>{timeAgo(b.created_at)}</span>
                    <button data-testid={`broadcast-delete-${b.broadcast_id}`} onClick={() => setToDelete(b)} aria-label="Delete broadcast" className="opacity-60 hover:opacity-100">
                      <Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />
                    </button>
                  </div>
                </div>
                <p className="text-xs mt-1 whitespace-pre-wrap" style={{ color: "var(--text-2)" }}>{b.body}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="broadcast-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remove broadcast?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>It will no longer appear in users' notification bells.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="broadcast-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="broadcast-delete-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} className="bg-red-600 hover:bg-red-700 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
